// 字段映射工具函数
// 提供字段配置查询、验证、格式化等功能
import { 
  FieldConfig, 
  FieldGroup, 
  SelectOption, 
  ValidationResult,
  FilterCondition,
  SortCondition,
  ColumnConfig
} from '../types/field-mapping';
import { FIELD_MAPPING, FIELD_GROUPS, DEFAULT_VISIBLE_COLUMNS } from '../config/field-mapping';
import { DataFormatter } from './formatters';

export class FieldMappingUtils {
  /**
   * 获取字段配置
   */
  static getFieldConfig(fieldKey: string): FieldConfig | undefined {
    return FIELD_MAPPING[fieldKey];
  }

  /**
   * 获取所有字段配置
   */
  static getAllFields(): FieldConfig[] {
    return Object.values(FIELD_MAPPING);
  }

  /**
   * 根据分组获取字段
   */
  static getFieldsByGroup(groupKey: string): FieldConfig[] {
    return Object.values(FIELD_MAPPING).filter(field => field.group === groupKey);
  }

  /**
   * 获取可见字段
   */
  static getVisibleFields(visibleColumns?: string[]): FieldConfig[] {
    const columns = visibleColumns || DEFAULT_VISIBLE_COLUMNS;
    return columns.map(key => FIELD_MAPPING[key]).filter(Boolean);
  }

  /**
   * 获取可筛选字段
   */
  static getFilterableFields(): FieldConfig[] {
    return Object.values(FIELD_MAPPING).filter(field => field.filterable);
  }

  /**
   * 获取可排序字段
   */
  static getSortableFields(): FieldConfig[] {
    return Object.values(FIELD_MAPPING).filter(field => field.sortable);
  }

  /**
   * 获取可搜索字段
   */
  static getSearchableFields(): FieldConfig[] {
    return Object.values(FIELD_MAPPING).filter(field => field.searchable);
  }

  /**
   * 获取字段分组
   */
  static getFieldGroup(groupKey: string): FieldGroup | undefined {
    return FIELD_GROUPS[groupKey];
  }

  /**
   * 获取所有字段分组
   */
  static getAllGroups(): FieldGroup[] {
    return Object.values(FIELD_GROUPS).sort((a, b) => a.order - b.order);
  }

  /**
   * 按分组组织字段
   */
  static getFieldsByGroups(): Record<string, FieldConfig[]> {
    const result: Record<string, FieldConfig[]> = {};
    
    Object.values(FIELD_GROUPS).forEach(group => {
      result[group.key] = this.getFieldsByGroup(group.key);
    });

    return result;
  }

  /**
   * 验证字段值
   */
  static validateFieldValue(fieldKey: string, value: any): ValidationResult {
    const fieldConfig = this.getFieldConfig(fieldKey);
    if (!fieldConfig) {
      return {
        valid: false,
        errors: [`未知字段: ${fieldKey}`],
        warnings: []
      };
    }

    const errors: string[] = [];
    const warnings: string[] = [];

    // 必填验证
    if (fieldConfig.required && (value === null || value === undefined || value === '')) {
      errors.push(`${fieldConfig.label}是必填字段`);
    }

    // 类型验证
    if (value !== null && value !== undefined && value !== '') {
      switch (fieldConfig.type) {
        case 'number':
        case 'currency':
          if (isNaN(Number(value))) {
            errors.push(`${fieldConfig.label}必须是数字`);
          } else {
            const numValue = Number(value);
            if (fieldConfig.range) {
              const [min, max] = fieldConfig.range;
              if (numValue < min || numValue > max) {
                errors.push(`${fieldConfig.label}必须在 ${min} - ${max} 范围内`);
              }
            }
          }
          break;

        case 'percentage':
          if (isNaN(Number(value))) {
            errors.push(`${fieldConfig.label}必须是数字`);
          } else {
            const numValue = Number(value);
            if (numValue < 0 || numValue > 100) {
              warnings.push(`${fieldConfig.label}通常在 0-100 范围内`);
            }
          }
          break;

        case 'select':
          if (fieldConfig.options) {
            const validValues = fieldConfig.options.map(opt => opt.value);
            if (!validValues.includes(value)) {
              errors.push(`${fieldConfig.label}的值不在允许的选项中`);
            }
          }
          break;

        case 'date':
          if (fieldConfig.format === 'YYYY.MM') {
            const datePattern = /^\d{4}\.\d{2}$/;
            if (!datePattern.test(value)) {
              errors.push(`${fieldConfig.label}格式必须为 YYYY.MM`);
            }
          }
          break;
      }
    }

    // 自定义验证规则
    if (fieldConfig.validation) {
      fieldConfig.validation.forEach(rule => {
        switch (rule.type) {
          case 'pattern':
            if (value && rule.value && !rule.value.test(value)) {
              errors.push(rule.message);
            }
            break;
          case 'custom':
            if (rule.validator && !rule.validator(value)) {
              errors.push(rule.message);
            }
            break;
        }
      });
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * 格式化字段值
   */
  static formatFieldValue(fieldKey: string, value: any): string {
    const fieldConfig = this.getFieldConfig(fieldKey);
    if (!fieldConfig) {
      return String(value || '-');
    }

    return DataFormatter.formatValue(value, fieldConfig);
  }

  /**
   * 获取字段选项
   */
  static getFieldOptions(fieldKey: string): SelectOption[] {
    const fieldConfig = this.getFieldConfig(fieldKey);
    return fieldConfig?.options || [];
  }

  /**
   * 获取字段范围
   */
  static getFieldRange(fieldKey: string): [number, number] | undefined {
    const fieldConfig = this.getFieldConfig(fieldKey);
    return fieldConfig?.range;
  }

  /**
   * 构建表格列配置
   */
  static buildTableColumns(visibleColumns?: string[]): any[] {
    const fields = this.getVisibleFields(visibleColumns);
    
    return fields.map(field => ({
      title: field.label,
      dataIndex: field.key,
      key: field.key,
      width: field.width,
      sorter: field.sortable,
      filterable: field.filterable,
      render: (value: any) => this.formatFieldValue(field.key, value),
      ...this.getColumnProps(field)
    }));
  }

  /**
   * 获取列属性
   */
  private static getColumnProps(field: FieldConfig): any {
    const props: any = {};

    // 对齐方式
    if (field.type === 'number' || field.type === 'currency' || field.type === 'percentage') {
      props.align = 'right';
    }

    // 固定列
    if (field.key === 'factoryName' || field.key === 'productName') {
      props.fixed = 'left';
    }

    // 筛选配置
    if (field.filterable && field.options) {
      props.filters = field.options.map(opt => ({
        text: opt.label,
        value: opt.value
      }));
    }

    return props;
  }

  /**
   * 构建筛选条件
   */
  static buildFilterConditions(filters: Record<string, any>): FilterCondition[] {
    const conditions: FilterCondition[] = [];

    Object.entries(filters).forEach(([fieldKey, value]) => {
      const fieldConfig = this.getFieldConfig(fieldKey);
      if (!fieldConfig || !value) return;

      if (Array.isArray(value)) {
        conditions.push({
          field: fieldKey,
          operator: 'in',
          value,
          label: `${fieldConfig.label}: ${value.join(', ')}`
        });
      } else {
        conditions.push({
          field: fieldKey,
          operator: 'eq',
          value,
          label: `${fieldConfig.label}: ${value}`
        });
      }
    });

    return conditions;
  }

  /**
   * 构建排序条件
   */
  static buildSortConditions(sorter: any): SortCondition[] {
    if (!sorter || !sorter.field) return [];

    return [{
      field: sorter.field,
      order: sorter.order === 'descend' ? 'DESC' : 'ASC'
    }];
  }

  /**
   * 获取默认列配置
   */
  static getDefaultColumnConfig(): ColumnConfig[] {
    return DEFAULT_VISIBLE_COLUMNS.map((key, index) => ({
      key,
      visible: true,
      order: index,
      width: this.getFieldConfig(key)?.width
    }));
  }

  /**
   * 搜索字段
   */
  static searchFields(searchText: string): FieldConfig[] {
    if (!searchText) return [];

    const lowerSearchText = searchText.toLowerCase();
    
    return Object.values(FIELD_MAPPING).filter(field => 
      field.label.toLowerCase().includes(lowerSearchText) ||
      field.key.toLowerCase().includes(lowerSearchText) ||
      field.description?.toLowerCase().includes(lowerSearchText)
    );
  }

  /**
   * 获取字段统计信息
   */
  static getFieldStats(data: any[], fieldKey: string): any {
    const fieldConfig = this.getFieldConfig(fieldKey);
    if (!fieldConfig) return null;

    const values = data.map(item => item[fieldKey]).filter(v => v !== null && v !== undefined);
    
    const stats: any = {
      field: fieldKey,
      count: values.length,
      nullCount: data.length - values.length
    };

    if (fieldConfig.type === 'number' || fieldConfig.type === 'currency') {
      const numValues = values.filter(v => !isNaN(Number(v))).map(Number);
      if (numValues.length > 0) {
        stats.min = Math.min(...numValues);
        stats.max = Math.max(...numValues);
        stats.avg = numValues.reduce((a, b) => a + b, 0) / numValues.length;
        stats.sum = numValues.reduce((a, b) => a + b, 0);
      }
    }

    // 获取最常见的值
    const valueCount: Record<string, number> = {};
    values.forEach(value => {
      const key = String(value);
      valueCount[key] = (valueCount[key] || 0) + 1;
    });

    stats.topValues = Object.entries(valueCount)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10)
      .map(([value, count]) => ({ value, count }));

    stats.uniqueCount = Object.keys(valueCount).length;

    return stats;
  }
}
